import * as React from 'react';
import { useContext } from 'react'
import { WebSocketContext } from './WebSocketProvider'
import type { WebSocketConnection  } from './useWebSocket'

/**
 * Hook для доступа к WebSocket соединению из контекста
 * 
 * @returns {WebSocketConnection} Объект с состоянием и методами WebSocket
 * @throws {Error} Если хук используется вне WebSocketProvider
 * 
 * @example
 * ```tsx
 * const { isConnected, executiveData, sendMessage } = useWebSocketContext()
 * ```
 */
export const useWebSocketContext = (): WebSocketConnection => {
  const context = useContext(WebSocketContext)
  
  if (context === null) {
    throw new Error(
      'useWebSocketContext must be used within a WebSocketProvider. ' +
      'Please wrap your component with <WebSocketProvider>.\n' +
      'Example:\n' +
      '```tsx\n' +
      '<WebSocketProvider>\n' +
      '  <YourApp />\n' +
      '</WebSocketProvider>\n' +
      '```'
    )
  }
  
  return context
}

/**
 * Безопасная версия хука, которая не выбрасывает ошибку
 * 
 * @returns {WebSocketConnection | null} Объект WebSocket или null если провайдер не найден
 * 
 * @example
 * ```tsx
 * const connection = useWebSocketContextSafe()
 * if (connection) {
 *   // Используем connection
 * }
 * ```
 */
export const useWebSocketContextSafe = (): WebSocketConnection | null => {
  return useContext(WebSocketContext)
}

/**
 * Хук для проверки доступности WebSocket
 * 
 * @returns {boolean} true если WebSocket поддерживается браузером
 */
export const useWebSocketSupported = (): boolean => {
  if (typeof window === 'undefined') return false
  return 'WebSocket' in window || 'MozWebSocket' in window
}

/**
 * Хук для получения данных executive мониторинга
 * 
 * @returns {object} Данные и состояние загрузки
 */
export const useExecutiveData = () => {
  const context = useWebSocketContextSafe()
  
  return {
    data: context?.executiveData || [],
    isLoading: !context?.isConnected,
    isConnected: context?.isConnected || false,
    lastUpdate: context?.lastMessage?.timestamp,
    error: context?.error
  }
}

/**
 * Хук для подписки на определенные типы сообщений
 * 
 * @param {string} messageType Тип сообщения для подписки
 * @param {(data: unknown) => void} callback Функция обратного вызова
 */
export const useWebSocketMessage = <T = unknown>(
  messageType: string, 
  callback: (data: T) => void
) => {
  const { lastMessage } = useWebSocketContext()
  
  React.useEffect(() => {
    if (lastMessage?.type === messageType) {
      callback(lastMessage.data as T)
    }
  }, [lastMessage, messageType, callback])
}

/**
 * Хук для фильтрации сообщений по типу
 */
export const useWebSocketMessageFilter = (messageType: string) => {
  const { lastMessage } = useWebSocketContext()
  
  return React.useMemo(() => {
    if (lastMessage?.type === messageType) {
      return lastMessage.data
    }
    return null
  }, [lastMessage, messageType])
}